/**
 * Das Störungs-Gedächtnis des PCM-Wegs: zehn Minuten über die Antwort hinaus. (11.10.2026, Nachbesserung „Vorlesen hakt“)
 *
 * ## Der Anlass
 *
 * Der Startvorrat je Antwort (tts-pcm-vorrat.ts) half zu wenig: Journal 05.–11.10. mit den Datei-Zeilen, 31 gestörte Stücke —
 * nur 4 nach einem Aussetzer in DERSELBEN Antwort, 27 waren der erste Aussetzer ihrer Antwort. Dagegen hatten 13 der 31 einen
 * Aussetzer in den 10 Minuten davor: eine unruhige Strecke bleibt eine Weile unruhig, auch über Antworten hinweg. Das Schwerste
 * am 11.10. war ein Stromabriss (`strom:Load failed`, Lücken von 3,3–10,3 s) — ein Netzproblem am Gerät, das kein Vorrat abfängt.
 *
 * ## Die Regel
 *
 * Eine STÖRUNG ist ein Stück mit Lücke ≥ 1 s (`STOERUNG_LUECKE_MS`) oder ein Stromfehler mitten im Stück / vor dem ersten Paket
 * (`istStromFehler`). Danach gilt `GEDAECHTNIS_MS` (10 Minuten, ab der JÜNGSTEN Störung):
 *  (a) der angehobene Vorrat — 1,5 s nach einer Störung, 2,5 s nach zwei (tts-pcm-vorrat.ts) — gilt auch für das ERSTE Stück
 *      einer Antwort. Preis: der Start kommt rund 0,8 s später (bei 1,3-facher Echtzeit);
 *  (b) nach einem STROMFEHLER läuft die Wiedergabe über den Datei-Weg (`dateiWeg()`): die Datei wird vor dem Abspielen ganz
 *      geladen, ein Netzabriss mitten im Strom kann dort nicht zu einer Lücke im Ton führen — er verzögert höchstens den Start.
 *      Nach Ablauf der zehn Minuten (ohne neue Störung) wieder Strom.
 *
 * ⚠️ Das ist ein ZEITLICH BEGRENZTER Zustand im Speicher der Seite — KEIN Dauerzustand wie `pcmRueckfall` (tts-pcm-rueckfall.ts,
 *    sessionStorage, bis zum Ausschalten). Nichts davon liegt im Speicher des Browsers, im Konto oder auf dem Server: eine neu geladene
 *    Seite, ein anderes Gerät und jede andere Sitzung starten frisch. Der 50er-Rückfall bleibt daneben unverändert.
 * ⚠️ Die Zeit ist einspritzbar (`jetzt`), damit Tests das Fenster prüfen, ohne zehn Minuten zu warten.
 * ⚠️ Ein Aussetzer unter 1 s zählt hier NICHT (er zählt für den Vorrat der laufenden Antwort, `PcmVorrat`): sonst hielte jedes
 *    einzelne Stolpern die Strecke zehn Minuten lang für schlecht.
 */
import { VORRAT_NACH_AUSSETZER_S, VORRAT_UNRUHIG_S } from "./tts-pcm-vorrat.js";

/** So lange (ms) nach der jüngsten Störung gilt das Gedächtnis. */
export const GEDAECHTNIS_MS = 10 * 60_000;
/** Ein Stück mit mindestens so viel Lücke (ms, zusammen) ist eine Störung. */
export const STOERUNG_LUECKE_MS = 1000;
/** Ab so vielen Störungen im Fenster gilt der Deckel-Vorrat. */
const GEDAECHTNIS_UNRUHIG_AB = 2;

type StoerungsGrund = "aussetzer" | "strom";

/**
 * Ist der Grund, aus dem ein PCM-Stück auf die Datei wechselte, ein Fehler des STROMS (Netz)? `strom:…` / `strom-still` / `strom`
 * (riss mitten im Stück), `Load failed` (WebKit) / `Failed to fetch` (Chromium) / `network…` (Abruf scheiterte) und die Frist
 * auf das erste Paket. Kontext-, Pegel- und Absage-Gründe (`kontext-…`, `stumm`, `uhr-steht`, `cache`, …) gehören nicht dazu.
 */
export function istStromFehler(grund: string): boolean {
  return /^strom|load failed|failed to fetch|network|netzwerk|erstes-paket-frist/i.test(grund);
}

export class PcmGedaechtnis {
  private zeiten: number[] = [];
  private stromBis = 0;
  private grundZuletzt: StoerungsGrund | null = null;

  constructor(private readonly jetzt: () => number = () => Date.now()) {}

  /** Eine Störung ist aufgetreten. Ein Stromfehler schaltet zusätzlich den Datei-Weg für das Fenster ein. */
  stoerung(grund: StoerungsGrund): void {
    const t = this.jetzt();
    this.aufraeumen(t);
    this.zeiten.push(t);
    this.grundZuletzt = grund;
    if (grund === "strom") this.stromBis = t + GEDAECHTNIS_MS;
  }

  private aufraeumen(t: number): void {
    this.zeiten = this.zeiten.filter((z) => t - z < GEDAECHTNIS_MS);
    if (this.zeiten.length === 0) this.grundZuletzt = null;
    if (t >= this.stromBis) this.stromBis = 0;
  }

  /** Störungen im Fenster. */
  anzahl(): number {
    this.aufraeumen(this.jetzt());
    return this.zeiten.length;
  }

  /** Gilt das Gedächtnis gerade? */
  aktiv(): boolean {
    return this.anzahl() > 0;
  }

  /** Der Vorrat (s), den JEDES Stück jetzt mindestens haben soll — auch das erste. 0: keine Störung im Fenster. */
  vorratS(): number {
    const n = this.anzahl();
    if (n === 0) return 0;
    return n >= GEDAECHTNIS_UNRUHIG_AB ? VORRAT_UNRUHIG_S : VORRAT_NACH_AUSSETZER_S;
  }

  /** Läuft die Wiedergabe jetzt über die Datei (Stromfehler im Fenster)? */
  dateiWeg(): boolean {
    this.aufraeumen(this.jetzt());
    return this.stromBis > 0;
  }

  /** Wie viele Sekunden das Gedächtnis noch gilt (ab der jüngsten Störung); 0 ohne Störung. */
  restS(): number {
    const t = this.jetzt();
    this.aufraeumen(t);
    const juengste = this.zeiten[this.zeiten.length - 1];
    return juengste === undefined ? 0 : Math.max(0, Math.round((GEDAECHTNIS_MS - (t - juengste)) / 1000));
  }

  /** Die Journalzeile (nur Zahlen): wie lange es noch gilt, wodurch es ausgelöst wurde, wie viele Störungen, was es bewirkt. */
  zeile(wirkung: "neu" | "datei" | "vorrat"): string {
    return `[tts-pcm] gedaechtnis aktiv=${this.restS()}s grund=${this.grundZuletzt ?? "-"} n=${this.anzahl()} wirkung=${wirkung}`;
  }

  /** Neu anfangen — der Schalter wurde aus- und wieder eingeschaltet, oder ein Test. */
  zuruecksetzen(): void {
    this.zeiten = [];
    this.stromBis = 0;
    this.grundZuletzt = null;
  }
}

/** Das eine Gedächtnis dieser Seite (nur im Speicher). */
export const pcmGedaechtnis = new PcmGedaechtnis();
